import { useState, useEffect, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API, useAuth } from "../../App";
import {
  ShieldCheck, ShieldAlert, Users,
  CheckCircle, TrendingUp, MessageCircle
} from "lucide-react";import { useToast } from "@/hooks/use-toast";

const Attribution = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clp/attribution-stats`, { headers });
      setStats(res.data);
    } catch (err) {
      console.error("Failed to fetch attribution stats:", err);
    }
    setLoading(false);
  }, [headers]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const confirmAgent = async (leadId) => {
    setConfirming(leadId);
    try {
      await axios.put(`${API}/clp/confirm-agent/${leadId}`, {}, { headers });
      toast({ title: "Agent registration confirmed" });
      fetchStats();
    } catch {
      toast({ title: "Failed to confirm", variant: "destructive" });
    }
    setConfirming(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2342]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="attribution-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#0A2342] font-['Playfair_Display']" data-testid="attribution-title">
          Commission Attribution
        </h1>
        <p className="text-slate-600">Track and protect your commission on active leads</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0 }}
          className="bg-white rounded-xl border border-slate-200 p-5" data-testid="stat-total-leads">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-5 h-5 text-slate-400" />
            <span className="text-xs text-slate-400">Total</span>
          </div>
          <p className="text-3xl font-bold text-[#0A2342]">{stats?.total_leads || 0}</p>
          <p className="text-xs text-slate-500 mt-1">All Leads</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="bg-white rounded-xl border border-green-200 p-5" data-testid="stat-protected">
          <div className="flex items-center justify-between mb-2">
            <ShieldCheck className="w-5 h-5 text-green-500" />
            <span className="text-xs text-green-600">Protected</span>
          </div>
          <p className="text-3xl font-bold text-green-600">{stats?.acknowledged || 0}</p>
          <p className="text-xs text-slate-500 mt-1">{stats?.acknowledgment_rate || 0}% rate</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-white rounded-xl border border-red-200 p-5" data-testid="stat-at-risk">
          <div className="flex items-center justify-between mb-2">
            <ShieldAlert className="w-5 h-5 text-red-500" />
            <span className="text-xs text-red-600">At Risk</span>
          </div>
          <p className="text-3xl font-bold text-red-600">{stats?.touring_without_ack || 0}</p>
          <p className="text-xs text-slate-500 mt-1">Touring, not acknowledged</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="bg-white rounded-xl border border-blue-200 p-5" data-testid="stat-high-intent">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-5 h-5 text-blue-500" />
            <span className="text-xs text-blue-600">High Intent</span>
          </div>
          <p className="text-3xl font-bold text-blue-600">{stats?.high_intent || 0}</p>
          <p className="text-xs text-slate-500 mt-1">Score 7+</p>
        </motion.div>
      </div>

      {/* At-Risk Leads Table */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="at-risk-table">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-red-500" />
          <h2 className="text-lg font-semibold text-[#0A2342]">At-Risk Leads</h2>
          <span className="ml-auto text-xs bg-red-100 text-red-600 px-2.5 py-1 rounded-full font-medium">
            {stats?.at_risk_leads?.length || 0} leads
          </span>
        </div>

        {!stats?.at_risk_leads?.length ? (
          <div className="p-8 text-center">
            <ShieldCheck className="w-10 h-10 text-green-400 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">All touring leads have acknowledged!</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {stats.at_risk_leads.map((lead) => (
              <div key={lead.id} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`at-risk-lead-${lead.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#0A2342] truncate">{lead.name}</p>
                  <p className="text-xs text-slate-500">{lead.email}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full capitalize">
                    {lead.stage}
                  </span>
                  <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded-full">
                    Score: {lead.tour_intent_score || 0}
                  </span>
                  {lead.phone && (
                    <a
                      href={`sms:${lead.phone}?body=Hi%20${encodeURIComponent(lead.name?.split(' ')[0] || '')}%2C%20just%20a%20reminder%20to%20list%20Bryan%20Spann%20-%20Texas%20United%20Realty%20when%20touring!`}
                      className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Send SMS reminder"
                      data-testid={`sms-remind-${lead.id}`}
                    >
                      <MessageCircle className="w-4 h-4" />
                    </a>
                  )}
                  <button
                    onClick={() => confirmAgent(lead.id)}
                    disabled={confirming === lead.id}
                    className="px-3 py-1.5 bg-green-500 text-white text-xs font-medium rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 flex items-center gap-1"
                    data-testid={`confirm-agent-${lead.id}`}
                  >
                    {confirming === lead.id ? (
                      <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                    ) : (
                      <>
                        <CheckCircle className="w-3 h-3" /> Confirmed
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Attribution;
